import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { getColorStyles } from '@risksmart-app/components/src/utils/colours';
import { type Ref, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboardWidgetSettings } from 'src/context/useDashboardWidgetSettings';
import type { ControlTestTableFields } from 'src/pages/controls/control-tests/types';

import { useGetWidgetData } from '../../gigawidget/hooks/useGetWidgetData';
import type { DateFilterOptions } from '../../gigawidget/types';
import { toIsoBounds } from '../../gigawidget/util/filterHelpers';
import type { WidgetRef } from '../../types';
import { dataSources } from '../../universal-widget/data-sources';
import type { FilterSettings } from '../../universal-widget/util';
import { useWidgetSettingsHandler } from '../../widget-context/useWidgetSettingsHandler';
import LineChartWidget from '../line-chart-widget/LineChartWidget';
import { WidgetSettingsModal } from '../widget-settings-modal/WidgetSettingsModal';
import { sortByDateX } from './utils';

export const ControlTestResultsOverTime = (
  _props: unknown,
  ref: Ref<WidgetRef>
) => {
  const [showModal, setShowModal] = useState(false);
  const [settings, setSettings] = useDashboardWidgetSettings<FilterSettings>();
  const { t } = useTranslation(['common'], {
    keyPrefix: 'dashboard.widgets.controlTestResultsOverTime',
  });

  useWidgetSettingsHandler(ref, () => setShowModal(true));

  const dataSource = dataSources.controlTest;
  // COI-837 Piece A — this chart plots test results directly, so the dashboard
  // date range must reach `testResults(where:)` or the widget is time-unbounded.
  // `useGetWidgetTestResults` already honours `dateRange` on both legs via
  // `TEST_RESULT_DATE_PREDICATES.TestDate`; only the call site was unwired.
  // Passed as `seriesDateFilterOptions`, NOT `dateFilterOptions`: this widget's
  // registry entry sets `ignoreDashboardDateFilter: true`, which makes
  // `useDataSourceFilter` discard the whole `dateFilter` result. The pre-COI-600
  // code read `filters.dateRange` from the store itself and passed a raw
  // `variables.where.TestDate`, so the gate never applied to it — this seam
  // restores that. See `useSeriesDateFilter`.
  //
  // Explicitly typed (rather than inlined into `useGetWidgetData`'s options
  // object) so `field: 'TestDate'` is checked against the data source's own
  // dateRange literal union instead of being widened to `string` — inferring
  // `TDataSource` from a sibling property in the same call loses that narrowing.
  const seriesDateFilterOptions: DateFilterOptions<typeof dataSource> = {
    dateFilter: (dateRange, precision) => ({
      dateRange: { field: 'TestDate', ...toIsoBounds(dateRange, precision) },
    }),
  };
  const {
    tableProps: { allItems },
    loading,
  } = useGetWidgetData({
    dataSource,
    propertyFilterQuery: settings?.filtering,
    seriesDateFilterOptions,
  });

  const series = useControlTestResultsOverTime(allItems);

  const { getByValue: getEffectivenessByValue } = useRating('effectiveness');

  const handleSave = async (data: FilterSettings) => {
    setSettings(data);
  };

  return (
    <>
      {showModal && (
        <WidgetSettingsModal
          onDismiss={() => setShowModal(false)}
          onSave={handleSave}
          dataSource={dataSource}
        />
      )}
      <LineChartWidget
        series={series}
        isLoading={loading}
        xTitle={t('xTitle')}
        yTitle={t('yTitle')}
        tooltipYValueFormatter={(y) =>
          getEffectivenessByValue(y)?.label || String(y)
        }
        pointColorFormatter={(y) =>
          getColorStyles(getEffectivenessByValue(y)?.color)?.backgroundColor
        }
      />
    </>
  );
};

const useControlTestResultsOverTime = (
  data: readonly ControlTestTableFields[] | undefined
) => {
  const groupedByTest = data?.reduce(
    (acc, test) => {
      const testId = test.parent?.Id;
      const testTitle = test.parent?.Title;

      if (!testId || !testTitle) {
        return acc;
      }

      if (!acc[testId]) {
        acc[testId] = {
          name: testTitle,
          data: [],
        };
      }
      acc[testId].data.push({
        x: test.TestDate,
        y: test.OverallEffectiveness ?? 0,
      });

      return acc;
    },
    {} as Record<
      string,
      { name: string; data: Array<{ x: string; y: number }> }
    >
  );

  if (groupedByTest) {
    const values = Object.values(groupedByTest);

    values.forEach((test) => {
      test.data.sort(sortByDateX);
    });

    return values;
  }

  return undefined;
};
